import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SkipForward } from "lucide-react";
import { MusicEngine } from "@/lib/musicEngine";

const SaxIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" fill="none" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 5.5 8.5 4.2" stroke="#D4AF37" strokeWidth="2.4" />
    <path d="M8.5 4.2C11.5 3.8 13 5.6 13 8.5" stroke="#D4AF37" strokeWidth="2.6" />
    <path d="M13 8.5V22a4 4 0 0 0 8 0v-6" stroke="#D4AF37" strokeWidth="4" />
    <path d="M18.6 16.5h4.8l3.6-6.5H15.8z" fill="#D4AF37" stroke="#D4AF37" strokeWidth="1.2" />
    <circle cx="13" cy="12" r="1.1" fill="#0B192C" />
    <circle cx="13" cy="15.5" r="1.1" fill="#0B192C" />
    <circle cx="13" cy="19" r="1.1" fill="#0B192C" />
    <path d="M10 12.5h1.4M10 16h1.4" stroke="#D4AF37" strokeWidth="1.2" />
  </svg>
);

// Small saxophone button: soft site-made music on every page except the Sanctuary.
export const MusicToggle = () => {
  const { pathname } = useLocation();
  const engine = useRef(null);
  const [on, setOn] = useState(false);
  const [piece, setPiece] = useState("");
  const hidden = pathname.startsWith("/sanctuary");

  useEffect(() => {
    if (hidden && engine.current?.playing) { engine.current.stop(); setOn(false); }
  }, [hidden]);
  useEffect(() => () => engine.current?.stop(), []);

  if (hidden) return null;

  const toggle = async () => {
    if (!engine.current) engine.current = new MusicEngine();
    if (on) { engine.current.stop(); setOn(false); return; }
    await engine.current.play();
    setPiece(engine.current.pieceName);
    setOn(true);
  };

  return (
    <div className="fixed bottom-5 left-5 z-[90] flex flex-col-reverse items-start gap-2 sm:flex-row sm:items-center" data-testid="music-toggle">
      <button
        onClick={toggle}
        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-bsn-midnight/90 border-2 border-bsn-gold shadow-lg backdrop-blur-md hover:bg-bsn-midnight"
        aria-label={on ? "Turn music off" : "Turn soft music on"}
        aria-pressed={on}
        title={on ? "Music on — tap to turn off" : "Play soft music"}
        data-testid="music-toggle-btn"
      >
        <span className={`w-9 h-9 rounded-full flex items-center justify-center ring-2 ring-bsn-gold ${on ? "bg-bsn-navy" : "bg-bsn-midnight"}`}>
          <SaxIcon className="w-6 h-6" />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-xs font-semibold text-bsn-gold" data-testid="music-toggle-label">Music Player</span>
          <span className="text-[10px] text-bsn-champagne/80">{on ? "On · tap to turn off" : "Off · tap to play"}</span>
        </span>
      </button>
      {on && (
        <div className="flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-bsn-midnight/85 border border-bsn-gold/30 text-bsn-champagne text-xs shadow-lg" data-testid="music-now-playing">
          <span className="max-w-[9rem] truncate">{piece}</span>
          <button onClick={() => setPiece(engine.current.next())} className="w-7 h-7 rounded-full flex items-center justify-center hover:text-bsn-gold" aria-label="Next piece" data-testid="music-next-btn">
            <SkipForward className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
